import classNames from "classnames";
import React from "react";
import { defineMessages, useIntl } from "react-intl";
import Icon from "theme/components/atoms/Icon";
import useAriaAttributes from "theme/components/helpers/useAriaAttributes";
import useDataAttributes from "theme/components/helpers/useDataAttributes";
import Slider from "theme/components/organisms/Slider";
import { useSliderState } from "theme/components/organisms/Slider/Slider";
import CarouselSlide from "theme/components/organisms/Carousel/CarouselSlide";

const messages = defineMessages({
  previous: {
    id: "components.organisms.Carousel.previous",
    defaultMessage: "Previous",
  },
  next: {
    id: "components.organisms.Carousel.next",
    defaultMessage: "Next",
  },
  goToSlide: {
    id: "components.organisms.Carousel.goToSlide",
    defaultMessage: "Go to slide {number}",
  },
});

const Carousel = ({
  slides,
  children,
  content,
  imageSizes,
  showNavigation = true,
  showControls = true,
  ...otherProps
}) => {
  const dataAttributes = useDataAttributes(otherProps);
  const ariaAttributes = useAriaAttributes(otherProps);
  const intl = useIntl();

  const length = slides ? slides.length : React.Children.count(children);
  const sliderProps = useSliderState(length);

  const padStart = (value) => {
    return value
      .toString()
      .padStart(Math.max(2, length.toString().length), "0");
  };

  return (
    <div
      className={classNames("carousel", {
        "carousel--with-description": content,
      })}
      {...dataAttributes}
      {...ariaAttributes}
    >
      <div className="carousel__slider inverted">
        {length > 1 && (
          <div className="carousel__container container">
            {showControls && (
              <div className="carousel__controls">
                <button
                  onClick={sliderProps.showPrev}
                  disabled={sliderProps.state.nextIndex === 0}
                >
                  <Icon
                    icon="chevron-left"
                    title={intl.formatMessage(messages.previous)}
                  />
                </button>
                <button
                  onClick={sliderProps.showNext}
                  disabled={sliderProps.state.nextIndex === length - 1}
                >
                  <Icon
                    icon="chevron-right"
                    title={intl.formatMessage(messages.next)}
                  />
                </button>
              </div>
            )}

            {showNavigation && (
              <div className="carousel__nav">
                <div className="carousel__nav-start">{padStart(1)}</div>
                <div className="carousel__nav-buttons">
                  {new Array(length).fill(null).map((_, index) => (
                    <button
                      className={classNames("carousel__button", {
                        "carousel__button--current":
                          index === sliderProps.state.nextIndex,
                      })}
                      onClick={() => sliderProps.goTo(index)}
                      key={index}
                    >
                      {intl.formatMessage(messages.goToSlide, {
                        number: index + 1,
                      })}
                    </button>
                  ))}
                </div>
                <div className="carousel__nav-end">{padStart(length)}</div>
              </div>
            )}
          </div>
        )}
        <Slider {...sliderProps}>
          {slides
            ? slides.map((slideProps) => (
                <CarouselSlide {...slideProps} imageSizes={imageSizes} />
              ))
            : children}
        </Slider>
      </div>
      {content && (
        <div className="carousel__description">
          <div className="container">
            <div className="carousel__description-content">{content}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export { CarouselSlide };
export default Carousel;
